import type { ErrorEvent, EventHint } from '@sentry/react'
import * as Sentry from '@sentry/react'

import type { Router } from '@/main'
import type { RootSearch } from '@/routes/__root'
import { getLocaleFromPath } from '@/utils/base-path-params'
import { getPlatform } from '@/utils/get-platform'

const DSN = 'https://020e1fd306605cac8079e5969773291f@o514642.ingest.us.sentry.io/4510227376439296'
const INSIGHTS_LONG_TERM_ID_KEY = 'long_term_id'

const getExceptionMaybe = (hint: EventHint) => {
  const exception = hint.originalException || hint.syntheticException

  /**
   * Some exceptions from Sentry's Hint object will be a string named 'undefined',
   * instead of being an undefined object.
   */
  if (exception && exception !== 'undefined') {
    const castedError = exception as Error
    const typeGuard = castedError?.name && castedError?.message ? castedError : null
    return typeGuard
  }

  return null
}

const beforeSendEvent = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const exception = getExceptionMaybe(hint)
  // Only log to console if the event is not from console (to avoid double logging)
  if (event.logger !== 'console') {
    if (event.level === 'error') {
      console.error('Sentry exception:', exception)
    } else {
      // biome-ignore lint/suspicious/noConsole: <Logs captureMessage for example>
      console.log('Sentry message:', event, hint)
    }
  }

  if (import.meta.env.MODE !== 'production') {
    // Prevent sending events to Sentry in non-production environments, but still log them in the console.
    return null
  }

  return event
}

const disabledBeforeSendEvents = () => {
  if (import.meta.env.MODE !== 'production') {
    // Prevent sending any events to Sentry in non-production environments
    return {
      beforeBreadcrumb: () => null,
      beforeSendLog: () => null,
      beforeSendMetric: () => null,
      beforeSendTransaction: () => null,
    }
  }

  return undefined
}

export function initSentry(router: Router) {
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    ignoreErrors: [
      'Failed to fetch',
      'Failed to send .* event, TypeError: .*',
      'TypeError: Failed to fetch',
      'Load failed',
      'ConnectionProblemException',
      'GAID not found',
      'Error loading gtag',
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      Sentry.replayCanvasIntegration(),
      Sentry.replayIntegration({
        block: ['kompis-keyboard'],
        blockAllMedia: false,
        maskAllInputs: true,
        maskAllText: false,
      }),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    release: import.meta.env.VITE_APP_VERSION,
    // Session Replay
    replaysOnErrorSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    // Tracing
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0,
    beforeSend: beforeSendEvent,
    ...disabledBeforeSendEvents(),
  })

  setTags(router.latestLocation.search)
}

function setTags(search: RootSearch) {
  Sentry.setTag('betraktaVersion', __BETRAKTA_VERSION__)
  Sentry.setTag('motorVersion', __CORE_VERSION__)
  Sentry.setTag('kompisVersion', __KOMPIS_VERSION__)
  Sentry.setTag('platform', getPlatform(search))
  Sentry.setTag('locale', getLocaleFromPath())
  Sentry.setTag('storeId', search.storeId)
  Sentry.setTag('computerId', search.computerId)
}

export function setSentryUser({ storeId, computerId }: RootSearch = {}) {
  const id = getLongTermId() ?? getKioskId(storeId, computerId) ?? getRandomUserId()

  if (!id) return

  Sentry.setUser({ id })
}

/**
 * @see https://i-p-e-x.atlassian.net/wiki/spaces/IPEX/pages/2252210269/Session+logic+flow
 */
function getLongTermId(): string | null {
  try {
    const value = localStorage.getItem(INSIGHTS_LONG_TERM_ID_KEY)
    if (!value) return null

    const { id } = JSON.parse(value) as { id?: unknown }
    return typeof id === 'string' ? id : null
  } catch {
    return null
  }
}

/**
 * Builds a deterministic user ID from kiosk identifiers so that
 * events from the same kiosk can be correlated even without a
 * long-term analytics ID. Returns `null` when neither identifier
 * is available (i.e. non-kiosk sessions).
 */
function getKioskId(storeId?: string, computerId?: string): string | null {
  if (computerId && storeId) return `kiosk:${storeId}:${computerId}`
  if (computerId) return `kiosk:${computerId}`
  if (storeId) return `kiosk:${storeId}`
  return null
}

/**
 * Generates a random user ID using the Web Crypto API's `crypto.randomUUID()` method.
 * Returns `null` if the method is not available (e.g., in older browsers).
 * This function is used as a fallback when a long-term ID cannot be retrieved from localStorage.
 */
function getRandomUserId(): string | null {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') return null

  return crypto.randomUUID()
}
